/**
 * THE TARGET'S CURRENT STATE, AS AN ASSISTANT TURN CARRIES IT (task 813).
 *
 * Three of the Assistant's tiers work on an AI Service that already exists, and
 * none of them can look one up: the Composer guide has `tools: []`, and the UI
 * and test generators map their `endpointSpec` straight off the turn. So the
 * caller loads the target's current state and sends it — that is what
 * `targetSummary` is, and it is a DECLARED field of the Assistant's turn input
 * (`innovate-service/prisma/seed/assistant-collaborations.ts`, `TURN_INPUT`).
 * Until this shipped the sidebar sent neither it nor `baseArtifact`, so a modify
 * turn ran blind, and the interface and test tiers answered by asking for a spec
 * the app was holding all along.
 *
 * **One object, two readers, one shape.** The turn input declares a single
 * `targetSummary`, and the graph hands it to both kinds of consumer verbatim:
 *
 *   1. The Composer guide (modify mode) reads it NESTED — `targetSummary
 *      .endpoint.path` is copied character for character into the change spec,
 *      and `endpoint.description` is what a metadata change compares against
 *      (the contract from the task numbered 808).
 *   2. The UI generator and the test generator read it FLAT, as `endpointSpec`:
 *      `{ accountSlug, apiBaseUrl, path, method, description, input, output,
 *      responseQueued, authPolicy, hasCollaboration }` — the same object the
 *      Test Composer surface builds from an endpoint row (`useTestComposer`).
 *
 * Rather than guess which tier the classifier will pick — the app cannot know,
 * because routing happens inside the collaboration — the summary carries both
 * readings of the same endpoint row. Nothing is invented for either: every value
 * below is a field of the `Endpoint` the app already holds.
 *
 * **What is deliberately NOT here.** `intent` and `shape` appear in the guide's
 * description of the summary, and the app has neither. `intent` is the guide's
 * own one-sentence note about behavior and is explicitly *not* the Description
 * (the task numbered 808's defect was exactly that conflation), so sending the
 * Description as `intent` would hand the guide a fabricated value to carry
 * forward. The guide's own rule for a field it cannot see is to omit it, and so
 * is this module's.
 */
import type { AuthPolicy, Endpoint } from '@/types';
import type { GeneratedEndpointUi } from '@/types/uiComposer';
import { resolveHeadVersion } from '@/lib/generatedUi';

/**
 * The target AI Service's current state at spec level.
 *
 * Flat fields first (read as `endpointSpec`), then the nested `endpoint` object
 * the Composer guide copies from. Both describe the same row.
 */
export interface AssistantTargetSummary {
  /** `{accountSlug}` in `/api/{accountSlug}/{path}` — half of the live URL. */
  accountSlug?: string | null;
  /**
   * The absolute API host a generated interface has to bake in: it runs in a
   * sandboxed iframe or on a foreign host, where a relative `/api/...` resolves
   * against the wrong origin (task 669).
   */
  apiBaseUrl: string;
  path: string;
  method: string;
  description: string | null;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  /** Whether callers poll for the result — the generated interface branches on it. */
  responseQueued: boolean;
  authPolicy: AuthPolicy | null;
  /** Whether a continuation affordance makes sense in a generated interface. */
  hasCollaboration: boolean;
  /** Which collaboration serves this AI Service — the config's identity, not its definition. */
  collaborationConfigId: string | null;
  /**
   * The same service as the modify guide reads it. `path` is the live address
   * callers already use, which is why it travels verbatim: a guessed one shows
   * up on the confirmation card as a path change nobody asked for.
   */
  endpoint: {
    path: string;
    method: string;
    name: string | null;
    description: string | null;
  };
  /**
   * Each Agent of the service and the knowledge bases attached to it today, by
   * name (task 1079), so the modify guide can describe what the service answers
   * from now and compute the change. Present only once every Agent's list has
   * loaded; absent means "not known", never "none".
   */
  agents?: AssistantTargetAgentKnowledge[];
}

/** One Agent of the target service and its attached knowledge bases, by name. */
export interface AssistantTargetAgentKnowledge {
  name: string;
  knowledgeBaseNames: string[];
}

/**
 * A saved interface to revise rather than regenerate — exactly the shape the UI
 * branch and its save step take: `source` is what the generator starts from, and
 * `generatedUiId` is what makes the save an update of that interface instead of
 * a new one.
 */
export interface AssistantBaseArtifact {
  generatedUiId: string;
  source: string;
}

/** The two optional target fields a resource-bound turn carries. */
export interface AssistantTargetFields {
  targetSummary?: AssistantTargetSummary;
  baseArtifact?: AssistantBaseArtifact;
}

/** Build the summary from an endpoint row the app already holds. */
export function buildAssistantTargetSummary(
  endpoint: Endpoint,
  apiBaseUrl: string,
  agents?: AssistantTargetAgentKnowledge[]
): AssistantTargetSummary {
  return {
    accountSlug: endpoint.accountSlug ?? null,
    apiBaseUrl,
    path: endpoint.path,
    method: endpoint.method,
    description: endpoint.description,
    input: endpoint.input,
    output: endpoint.output,
    responseQueued: endpoint.responseQueued,
    authPolicy: endpoint.authPolicy ?? null,
    hasCollaboration: Boolean(endpoint.collaborationConfigId),
    collaborationConfigId: endpoint.collaborationConfigId,
    endpoint: {
      path: endpoint.path,
      method: endpoint.method,
      name: endpoint.name,
      description: endpoint.description,
    },
    ...(agents ? { agents } : {}),
  };
}

/**
 * The service's current interface, as a revision base — or undefined when it has
 * none.
 *
 * `GET /generated-endpoint-uis` returns PARENT rows with their versions nested,
 * and `source` exists only on a version, so the hop through `resolveHeadVersion`
 * is not optional: reading `source` off a parent is the defect that once left
 * the Interface card empty (task 396).
 */
export function buildAssistantBaseArtifact(
  uis: GeneratedEndpointUi[] | undefined
): AssistantBaseArtifact | undefined {
  const parent = uis?.[0];
  if (!parent) return undefined;
  const head = resolveHeadVersion(parent);
  if (!head?.source) return undefined;
  return { generatedUiId: parent.id, source: head.source };
}

/**
 * What the turn should carry, given the resource the turn is ABOUT and whatever
 * the app has loaded.
 *
 * The identity check is the point of this function, not a formality. Which
 * resource a turn targets is conversation state — a thread bound to a service
 * keeps that service even when the turn is sent from somewhere else (the target
 * stickiness from the task numbered 808) — while the loaded endpoint follows
 * whatever the app last fetched. If those two ever disagree, the honest answer
 * is to send NOTHING: a summary of the wrong service is worse than none, because
 * the guide copies a path out of it verbatim. Absent is a state every consuming
 * tier already handles by asking; wrong is a state none of them can detect.
 *
 * A general-context turn (no resource) therefore carries neither field, which is
 * also what keeps the help tier exactly as it was.
 */
export function assistantTargetFields(input: {
  resourceType?: string;
  resourceId?: string;
  endpoint: Endpoint | undefined;
  generatedUis: GeneratedEndpointUi[] | undefined;
  apiBaseUrl: string;
  /** The target's Agents and their attached bases, once loaded (task 1079). */
  agentKnowledge?: AssistantTargetAgentKnowledge[];
}): AssistantTargetFields {
  const { resourceType, resourceId, endpoint, generatedUis, apiBaseUrl, agentKnowledge } = input;

  // Only an AI Service has a spec-level summary. Other resource types (agents,
  // prompts, configs) are the Resource Reviewer's subject, and it reads them
  // for itself through the management MCP.
  if (resourceType !== 'endpoint' || !resourceId) return {};
  if (!endpoint || endpoint.id !== resourceId) return {};

  const baseArtifact = buildAssistantBaseArtifact(generatedUis);
  return {
    targetSummary: buildAssistantTargetSummary(endpoint, apiBaseUrl, agentKnowledge),
    ...(baseArtifact ? { baseArtifact } : {}),
  };
}
